import { useState, useMemo } from "react"
import { RatingRow } from "@/components/planning/RatingRow"
import {
  AUDIT_CATEGORIES,
  AUDIT_QUESTIONS,
  AUDIT_OPTIONAL,
  QUESTIONS_PER_CATEGORY,
} from "@/components/planning/audit-questions"

// Build initial scores object from server-persisted scores or empties.
//
// A stored `null` at a position listed in AUDIT_OPTIONAL[key] means the
// member previously marked that statement N/A (the server normalizes the
// "na" wire sentinel to `nil` before persisting — see
// QuarterlyCheckIn.normalize_audit_scores). Rehydrate that as the "na"
// state sentinel so it renders as "marked N/A" rather than "untouched." A
// stored `null` at a non-optional position stays `null` (untouched), same
// as before.
//
// Guarded on `hasSavedArray`: a brand-new QuarterlyCheckIn has
// `audit_scores` as nil (checked via `.blank?` throughout the controller,
// e.g. intake_controller.rb's `audit_scores.blank?` gates), not a hash of
// all-null arrays — so `savedScores[key]` is simply absent, not "an array
// with an explicit null." Without this guard, a member who has never
// touched the form at all would see the two optional family statements
// pre-selected as N/A before making any choice, which is wrong — they
// should start untouched like every other statement, same as R5 requires.
function initialScores(savedScores) {
  const out = {}
  AUDIT_CATEGORIES.forEach(({ key }) => {
    const hasSavedArray = Array.isArray(savedScores?.[key])
    const arr = hasSavedArray ? savedScores[key] : []
    const optionalIdx = AUDIT_OPTIONAL[key] || []
    out[key] = Array.from({ length: QUESTIONS_PER_CATEGORY }, (_, i) => {
      const raw = arr[i] || null
      return raw == null && hasSavedArray && optionalIdx.includes(i) ? "na" : raw
    })
  })
  return out
}

const TOTAL_QUESTIONS = AUDIT_CATEGORIES.length * QUESTIONS_PER_CATEGORY
const MAX_CATEGORY_SCORE = QUESTIONS_PER_CATEGORY * 5

// Mirrors the server's QuarterlyCheckIn.category_score proration formula
// (app/models/quarterly_check_in.rb) so the on-screen running total never
// visibly contradicts the score that actually gets stored. An "na" answer
// (or an untouched `null`) does not contribute 0 to the total the way a
// naive `Number(v) || 0` sum would — it's excluded from the average and
// the category is scaled back up, same as the server does.
function categoryScore(values) {
  const answered = values.filter((v) => typeof v === "number")
  const clamp5 = (v) => Math.min(5, Math.max(0, v))
  if (answered.length === values.length) {
    return answered.reduce((sum, v) => sum + clamp5(v), 0)
  }
  if (answered.length === 0) return 0
  const avg = answered.reduce((sum, v) => sum + clamp5(v), 0) / answered.length
  return Math.min(MAX_CATEGORY_SCORE, Math.max(0, Math.round(avg * QUESTIONS_PER_CATEGORY)))
}

// Shared Integrated Life Audit form. Used by:
//   - Intake step `App/Plan/Intake/steps/Audit.jsx` (passes initialScores from
//     QuarterlyCheckIn.audit_scores)
//   - Standalone retake page `App/Member/BalanceWheel/Retake.jsx` (always
//     starts blank; submits to wheel_snapshots#create)
//
// Parent controls submit timing and message via the optional `footer` slot.
// `onChange` reports both raw scores and `complete` (all 30 answered) so the
// parent can disable a Submit button until the form is fully filled in.
export function AuditForm({
  initialScores: savedScores = {},
  onChange,
  footer = null,
}) {
  const [scores, setScores] = useState(() => initialScores(savedScores))

  const total = useMemo(() => {
    return AUDIT_CATEGORIES.reduce((sum, { key }) => sum + categoryScore(scores[key]), 0)
  }, [scores])

  const answered = useMemo(() => {
    return Object.values(scores)
      .flat()
      .filter((v) => v != null).length
  }, [scores])

  const setScore = (category, questionIdx, value) => {
    setScores((prev) => {
      const next = {
        ...prev,
        [category]: prev[category].map((v, i) => (i === questionIdx ? value : v)),
      }
      const nextAnswered = Object.values(next)
        .flat()
        .filter((x) => x != null).length
      onChange?.({
        scores: next,
        answered: nextAnswered,
        total: TOTAL_QUESTIONS,
        complete: nextAnswered === TOTAL_QUESTIONS,
      })
      return next
    })
  }

  return (
    <div>
      <div className="space-y-8">
        {AUDIT_CATEGORIES.map(({ key, label, description }) => (
          <div key={key}>
            <div className="mb-3">
              <h3 className="font-display text-lg text-zinc-900">{label}</h3>
              <p className="text-xs text-zinc-500">{description}</p>
            </div>
            <ul className="space-y-4">
              {AUDIT_QUESTIONS[key].map((question, qIdx) => (
                <li
                  key={qIdx}
                  className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-6"
                >
                  <p className="text-sm text-zinc-800 sm:flex-1">{question}</p>
                  <RatingRow
                    value={scores[key][qIdx]}
                    onChange={(v) => setScore(key, qIdx, v)}
                    ariaLabel={`${label}: ${question}`}
                    allowNA={Boolean(AUDIT_OPTIONAL[key]?.includes(qIdx))}
                  />
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
      <p className="text-xs text-zinc-400 mt-6 text-right">
        Running total: {total}/{TOTAL_QUESTIONS * 5}
      </p>
      {footer}
    </div>
  )
}

AuditForm.TOTAL_QUESTIONS = TOTAL_QUESTIONS
